import { CloseIcon } from 'assets/icons/CloseIcon'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { Button } from 'components/ui/Button/Button'
import { ISelectValue } from 'constants/globalTypes'
import { format } from 'date-fns'
import useModalClose from 'hooks/useModalClose'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { CursorPointer, Flex } from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import { IFuelTransferModalState, IFuelTransferSavePayload } from 'types/fuelCustody.interface'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { fuelDeliveryFuelTypeLabel } from '../fuelData'

export interface IFuelTransferTankOption extends ISelectValue {
  fuelType?: number
  currentAmount?: number
  capacity?: number
}

export const TransferModal = ({
  closeModal,
  tankOptions,
  defaultFromId,
  onSave,
}: {
  closeModal: any
  tankOptions: IFuelTransferTankOption[]
  defaultFromId: number | null
  onSave: (payload: IFuelTransferSavePayload) => void
}) => {
  const [state, setState] = useState<IFuelTransferModalState>({
    fromTank: tankOptions.find((option) => option.value === defaultFromId) || null,
    toTank: null,
    litres: null,
    date: new Date(),
    time: format(new Date(), 'HH:mm'),
    note: '',
  })
  useFocusFirstInput()
  useModalClose(closeModal)

  const sameTank =
    state.fromTank != null && state.toTank != null && state.fromTank.value === state.toTank.value

  const fromFuelType = (state.fromTank as IFuelTransferTankOption | null)?.fuelType
  const fromOptions = tankOptions.filter((option) => option.value !== state.toTank?.value)
  const toOptions = tankOptions.filter((option) => {
    if (option.value === state.fromTank?.value) return false
    if (fromFuelType != null && option.fuelType != null && option.fuelType !== fromFuelType) {
      return false
    }
    return true
  })

  const invalid =
    !state.fromTank || !state.toTank || sameTank || !state.date || !state.litres || state.litres <= 0

  const invalidMessage = (): string | null => {
    if (!state.fromTank) return 'Pick a source tank'
    if (!state.toTank) return 'Pick a destination tank'
    if (sameTank) return 'From and to tank must differ'
    if (!state.litres || state.litres <= 0) return 'Enter litres greater than zero'
    if (!state.date) return 'Pick a transfer day'
    return null
  }

  const handleSave = () => {
    if (invalid) {
      const message = invalidMessage()
      if (message) toast.warn(message)
      return
    }
    const to = state.toTank as IFuelTransferTankOption
    const from = state.fromTank as IFuelTransferTankOption
    if (from.fuelType != null && to.fuelType != null && from.fuelType !== to.fuelType) {
      toast.warn(
        `Cannot transfer ${fuelDeliveryFuelTypeLabel(from.fuelType)} into a tank that holds ${fuelDeliveryFuelTypeLabel(to.fuelType)}.`,
      )
      return
    }
    if (
      to.capacity != null &&
      to.currentAmount != null &&
      Number(to.currentAmount) + Number(state.litres) > Number(to.capacity)
    ) {
      toast.warn(
        `Transfer would exceed destination tank capacity (${to.capacity}L). Current level: ${to.currentAmount}L, transferring: ${state.litres}L.`,
      )
      return
    }
    onSave({
      fromTankId: Number(state.fromTank.value),
      toTankId: Number(state.toTank.value),
      litres: state.litres,
      dateISO: format(state.date, 'yyyy-MM-dd'),
      time: state.time,
      note: state.note,
    })
    closeModal()
  }

  return (
    <Modal black>
      <Flex column between center>
        <Flex column gap='1rem' width='100%'>
          <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
            <TitleText weight='bold'>Record transfer</TitleText>
            <CursorPointer onClick={closeModal}>
              <CloseIcon />
            </CursorPointer>
          </Flex>
          <HonestSelectInput
            labelText='From tank'
            name='fromTank'
            options={fromOptions}
            defaultValue={state.fromTank?.value}
            onChange={(value: ISelectValue) =>
              setState({
                ...state,
                fromTank: value,
                toTank:
                  state.toTank &&
                  (value as IFuelTransferTankOption).fuelType != null &&
                  (state.toTank as IFuelTransferTankOption).fuelType != null &&
                  (value as IFuelTransferTankOption).fuelType !==
                    (state.toTank as IFuelTransferTankOption).fuelType
                    ? null
                    : state.toTank,
              })
            }
            nomarg
            required
            invalid={!state.fromTank}
          />
          <HonestSelectInput
            labelText='To tank'
            name='toTank'
            options={toOptions}
            defaultValue={state.toTank?.value}
            onChange={(value: ISelectValue) => setState({ ...state, toTank: value })}
            nomarg
            required
            invalid={!state.toTank || sameTank}
          />
          {sameTank && <OtherText grey>From and to tank must differ.</OtherText>}
          {fromFuelType != null && (
            <OtherText grey>
              {`Only tanks holding ${fuelDeliveryFuelTypeLabel(fromFuelType)} are shown as destinations.`}
            </OtherText>
          )}
          <Flex gap='1rem'>
            <Input
              placeholder='3000...'
              labelText='Litres'
              name='litres'
              type='number'
              value={state.litres}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setState({
                  ...state,
                  litres: e.target.value === '' ? null : Number(e.target.value),
                })
              }
              nomarg
              required
              invalid={!state.litres || state.litres <= 0}
            />
            <Input
              placeholder='06:10'
              labelText='Time'
              name='time'
              value={state.time}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setState({ ...state, time: e.target.value })
              }
              nomarg
            />
          </Flex>
          <CalendarInput
            labelText='Day'
            name='transferDate'
            defaultValue={state.date}
            maxDate={new Date()}
            onChange={(date: Date | null) => setState({ ...state, date })}
            nomarg
            required
          />
          <Input
            placeholder='Bowser load for Pit circuits...'
            labelText='Note'
            name='note'
            value={state.note}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setState({ ...state, note: e.target.value })
            }
            nomarg
          />
        </Flex>
        <Button style={{ marginTop: '1rem' }} variant='primary' onClick={handleSave}>
          Save
        </Button>
      </Flex>
    </Modal>
  )
}
